'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Maximize2,
  Minimize2,
  RotateCcw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { formatFileSize } from '@kit/shared/lib/file-utilities';

import { cn } from '../../lib/utils';
import { ImageCanvas } from './image-canvas';
import {
  DEFAULT_IMAGE_VIEWER_TRANSLATIONS,
  DEFAULT_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  ZOOM_STEP,
  type ImageViewerTranslations,
  type ViewerImage,
} from './types';

interface ImageLightboxProps {
  images: ViewerImage[];
  /** Index of the currently shown image (controlled). */
  index: number;
  onIndexChange: (index: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  translations?: ImageViewerTranslations;
  /** Show the download action in the top bar (default true). */
  showDownload?: boolean;
}

const OVERLAY_ANIMATION =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0';

/**
 * Immersive fullscreen image lightbox following the Mobbin best-practice
 * pattern (Dropbox / Google Drive / adidas): dimmed backdrop, large side
 * arrows, floating bottom-center zoom toolbar, thumbnail filmstrip, pan-on-drag.
 *
 * Keyboard: ESC close · ← → navigate · + - zoom · 0 reset · f fullscreen.
 */
export function ImageLightbox({
  images,
  index,
  onIndexChange,
  open,
  onOpenChange,
  translations,
  showDownload = true,
}: ImageLightboxProps) {
  const t = { ...DEFAULT_IMAGE_VIEWER_TRANSLATIONS, ...translations };
  const contentRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [resetKey, setResetKey] = useState(`${index}-${open}`);

  const total = images.length;
  const current = images[index] ?? null;
  const hasPrevious = index > 0;
  const hasNext = index < total - 1;
  const canNavigate = total > 1;

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next > total - 1) return;
      onIndexChange(next);
    },
    [onIndexChange, total],
  );

  const zoomTo = useCallback(
    (value: number) => setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value))),
    [],
  );
  const zoomIn = useCallback(() => zoomTo(zoom + ZOOM_STEP), [zoom, zoomTo]);
  const zoomOut = useCallback(() => zoomTo(zoom - ZOOM_STEP), [zoom, zoomTo]);
  const resetZoom = useCallback(() => zoomTo(DEFAULT_ZOOM), [zoomTo]);

  // Reset zoom whenever the shown image changes or the lightbox (re)opens
  // (adjust-state-during-render, not an effect).
  const nextResetKey = `${index}-${open}`;
  if (nextResetKey !== resetKey) {
    setResetKey(nextResetKey);
    setZoom(DEFAULT_ZOOM);
  }

  const toggleFullscreen = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Keyboard shortcuts (ESC is handled by Radix → onOpenChange).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      switch (e.key) {
        case 'ArrowLeft':
          if (hasPrevious) {
            e.preventDefault();
            goTo(index - 1);
          }
          break;
        case 'ArrowRight':
          if (hasNext) {
            e.preventDefault();
            goTo(index + 1);
          }
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    open,
    index,
    hasPrevious,
    hasNext,
    goTo,
    zoomIn,
    zoomOut,
    resetZoom,
    toggleFullscreen,
  ]);

  if (!current) return null;

  const alt = current.alt ?? current.fileName ?? '';
  const downloadHref = current.downloadUrl ?? current.src;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn('fixed inset-0 z-50 bg-black/90', OVERLAY_ANIMATION)}
        />
        <DialogPrimitive.Content
          ref={contentRef}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'fixed inset-0 z-50 flex flex-col overflow-hidden bg-black text-white outline-none',
            OVERLAY_ANIMATION,
          )}
        >
          {/* Top bar */}
          <div className="relative z-10 flex shrink-0 items-start justify-between gap-4 p-4">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-sm font-medium">
                {current.fileName ?? (alt || ' ')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-white/60">
                {current.fileSize ? formatFileSize(current.fileSize) : null}
              </DialogPrimitive.Description>
            </div>

            {canNavigate && (
              <div
                className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm tabular-nums"
                aria-hidden
              >
                {index + 1} / {total}
              </div>
            )}

            <div className="flex items-center gap-1">
              {showDownload && (
                <a
                  href={downloadHref}
                  download={current.fileName ?? true}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t.download}
                  className="rounded-full p-2 transition-colors hover:bg-white/10"
                >
                  <Download className="h-5 w-5" />
                </a>
              )}
              <DialogPrimitive.Close
                aria-label={t.close}
                className="rounded-full p-2 transition-colors hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Image stage */}
          <div className="relative flex min-h-0 flex-1 px-4">
            {canNavigate && (
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                disabled={!hasPrevious}
                aria-label={t.previous}
                className="absolute top-1/2 left-4 z-10 -translate-y-1/2 rounded-full bg-black/40 p-2 transition-colors hover:bg-black/60 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            <ImageCanvas
              key={current.id ?? current.src}
              src={current.src}
              alt={alt}
              zoom={zoom}
              className="p-2"
            />

            {canNavigate && (
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                disabled={!hasNext}
                aria-label={t.next}
                className="absolute top-1/2 right-4 z-10 -translate-y-1/2 rounded-full bg-black/40 p-2 transition-colors hover:bg-black/60 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Floating zoom toolbar */}
          <div className="relative z-10 flex shrink-0 justify-center pb-4">
            <div className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 backdrop-blur">
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom <= MIN_ZOOM}
                aria-label={t.zoomOut}
                className="rounded-full p-2 transition-colors hover:bg-white/10 disabled:opacity-30"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="min-w-[3rem] text-center text-xs tabular-nums">
                {zoom}%
              </span>
              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom >= MAX_ZOOM}
                aria-label={t.zoomIn}
                className="rounded-full p-2 transition-colors hover:bg-white/10 disabled:opacity-30"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              {zoom !== DEFAULT_ZOOM && (
                <button
                  type="button"
                  onClick={resetZoom}
                  aria-label={t.resetZoom}
                  className="rounded-full p-2 transition-colors hover:bg-white/10"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
              <span className="mx-1 h-5 w-px bg-white/20" aria-hidden />
              <button
                type="button"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? t.exitFullscreen : t.fullscreen}
                className="rounded-full p-2 transition-colors hover:bg-white/10"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Thumbnail filmstrip */}
          {canNavigate && (
            <div className="relative z-10 mx-auto flex max-w-full shrink-0 justify-center gap-2 overflow-x-auto px-4 pb-4">
              {images.map((image, i) => (
                <button
                  key={image.id ?? image.src}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`${t.next} ${i + 1}`}
                  aria-current={i === index}
                  className={cn(
                    'relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border-2 transition',
                    i === index
                      ? 'border-white'
                      : 'border-transparent opacity-50 hover:opacity-100',
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={image.thumbnailSrc ?? image.src}
                    alt={image.alt ?? image.fileName ?? ''}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
